import React from 'react';
import {Editor} from '@tinymce/tinymce-react';
import {uploadFile} from "../../requests/fileRequests";
import {editorApiKey} from "../../consts/consts";
import {editorContentStyle, CONTENT_FONT_STACK} from "./editorContentStyle";
import {normalizeContentHtml} from "./normalizeContentHtml";
import {isWordListHtml, rebuildWordListsFromClipboard} from "./wordListPaste";

// TinyMCE's default for its Word filter, plus `start` on <ol>: the filter keeps
// no attributes on lists, and the lists rebuilt from a Word paste carry the
// number they continue from.
const WORD_VALID_ELEMENTS = '-strong/b,-em/i,-u,-span,-p,-ol[start],-ul,-li,-h1,-h2,-h3,-h4,-h5,-h6,'
    + '-p/div,-a[href|name],sub,sup,strike,br,del,table[width],tr,td[colspan|rowspan|width],'
    + 'th[colspan|rowspan|width],thead,tfoot,tbody';

export default React.forwardRef((props, ref) => {
    const uploadImage = (blobInfo, success, failure) => {
        uploadFile({
            fileName: (Math.random() * 100000000000000) + blobInfo.filename(),
            base64File: `data:${blobInfo.blob().type};base64,${blobInfo.base64()}`,
            folder: '/training'
        }).then(res => {
            const uploaded = res && res.data && res.data.responses && res.data.responses[0];
            if (uploaded && uploaded.success) {
                success(uploaded.data.url);
                return;
            }
            failure(`Error: ${uploaded && uploaded.message || 'Unknown error'}`, {remove: true});
        });
    }

    const addVideo = (data) => {
        return (
        `<video width=${data.width}
                height=${data.height}
                ${data.poster ? `poster=${data.poster}` : ''}
                controls=controls>
                <source src=${data.source}
                        ${data.sourcemime ? `type=${data.sourcemime}` : ''}
                />
                ${data.altsource ? 
                    `<source src=${data.altsource}
                             ${data.altsourcemime ? `type=${data.altsourcemime}` : ''}
                    />`
                    :
                    ''
                }
        </video>`
        );
    }

    // Word pastes arrive with their own font/size/spacing and, more damagingly,
    // with nested lists placed beside the <li> they belong to — which browsers
    // render as a fresh list starting back at 1. Repair the fragment before it
    // enters the document so the saved HTML is already correct.
    const cleanPaste = (plugin, args) => {
        args.content = normalizeContentHtml(args.content, {fromPaste: true});
    }

    // Word's clipboard HTML has to be restructured before TinyMCE's own Word
    // filter sees it: the filter strips the mso-list levels that say how deep
    // each item sits, and its fake-list conversion cannot see the real lists
    // around a fake item, so every level-3 item surfaced as a new top-level
    // number. This runs ahead of the plugin's handler. Preventing the default
    // makes that handler discard its paste bin and put the selection back, and
    // the rebuilt content then takes the normal paste route on the next tick.
    const interceptWordPaste = (editor) => (e) => {
        const data = e.clipboardData;
        if (!data || typeof data.getData !== 'function') return;

        let html = '';
        try {
            html = data.getData('text/html');
        } catch (err) {
            return;
        }
        if (!isWordListHtml(html)) return;

        const rebuilt = rebuildWordListsFromClipboard(html);
        if (!rebuilt) return;

        e.preventDefault();
        setTimeout(() => {
            if (editor.removed) return;
            editor.execCommand('mceInsertClipboardContent', false, {content: rebuilt});
        }, 0);
    }

    return (
        <div className={`EditorWrapper ${props.className}`}>
            <Editor
                apiKey={editorApiKey}
                onInit={(evt, editor) => ref.current = editor}
                initialValue={props.content}
                init={{
                    selector: 'textarea',
                    menubar: [
                        'file', 'edit', 'insert',
                    ],
                    plugins: [
                        'advlist anchor autosave autolink autoresize charmap codesample emoticons hr insertdatetime image imagetools fullscreen',
                        'link lists media pagebreak preview paste quickbars searchreplace table toc visualblocks wordcount',
                    ],
                    toolbar: [
                        'undo redo | formatselect fontselect fontsizeselect | bold italic underline strikethrough | superscript subscript | forecolor backcolor removeformat',
                        'alignleft aligncenter alignright alignjustify | numlist bullist | outdent indent | blockquote hr pagebreak | link anchor image media table toc | codesample charmap emoticons | visualblocks searchreplace preview | fullscreen',
                    ],
                    // Point sizes only, matching Word's own size menu, so a size
                    // chosen here means the same thing in a Word document.
                    fontsize_formats: '8pt 9pt 10pt 10.5pt 11pt 12pt 14pt 16pt 18pt 20pt 24pt 28pt 36pt 48pt',
                    font_formats: [
                        `Body (Calibri)=${CONTENT_FONT_STACK}`,
                        'Futura=Futura,Futura PT,sans-serif',
                        'Cambria=Cambria,Caladea,serif',
                        'Times New Roman=Times New Roman,Times,serif',
                        'Arial=Arial,Helvetica,sans-serif',
                        'Georgia=Georgia,serif',
                        'Courier New=Courier New,Courier,monospace',
                    ].join(';'),
                    quickbars_selection_toolbar: 'bold italic underline | formatselect | link blockquote',
                    quickbars_insert_toolbar: false,
                    autosave_ask_before_unload: true,
                    autosave_interval: '30s',
                    autosave_retention: '10m',
                    advlist_bullet_styles: 'default,circle,disc,square,lower-alpha,lower-roman',
                    default_link_target: '_blank',
                    link_default_protocol: 'https',
                    images_upload_handler: uploadImage,
                    // Allow pasting images straight from the clipboard (Ctrl/Cmd+V or
                    // screenshot paste). TinyMCE routes the pasted image through
                    // images_upload_handler (uploadImage) so it is stored as an uploaded
                    // file/URL just like a manual upload, not as a huge inline base64 blob.
                    paste_data_images: true,
                    automatic_uploads: true,
                    image_class_list: [
                        { title: 'Left', value: '' },
                        { title: 'Right', value: 'float-right' }
                    ],
                    // The editable body lives in an iframe the app stylesheet cannot
                    // reach, so the lesson typography is handed over explicitly.
                    // Without this the editor showed Helvetica/16px while the page
                    // published Futura/12pt — nothing an author laid out survived.
                    content_style: editorContentStyle,
                    // Keep Word's structure (headings, lists, tables, bold/italic)
                    // but not its inline font/size/spacing, which would otherwise
                    // override the lesson stylesheet on the published page.
                    paste_as_text: false,
                    paste_enable_default_filters: true,
                    paste_remove_styles_if_webkit: true,
                    paste_webkit_styles: 'none',
                    paste_merge_formats: true,
                    paste_preprocess: cleanPaste,
                    paste_word_valid_elements: WORD_VALID_ELEMENTS,
                    setup: (editor) => {
                        editor.on('paste', interceptWordPaste(editor), true);
                    },
                    // Word round-trips are the point of this editor, so ordered and
                    // bulleted lists must keep real <ol>/<ul> nesting rather than
                    // TinyMCE's flattened fallback.
                    lists_indent_on_tab: true,
                    video_template_callback: addVideo,
                    deprecation_warnings: false
                }}
            />
        </div>
    )
});
